"use client";

import { useEffect, useRef, RefObject } from "react";
import { gsap } from "gsap";

/**
 * Options for the bounce link animation hook
 */
export interface UseBounceLinkOptions {
  /** GSAP easing function (default: 'power3.easeOut') */
  ease?: string;
  /** Animation duration in seconds (default: 2) */
  duration?: number;
  /** Background color for the expanding circle on hover. Set to false or 'transparent' to hide the circle */
  hoverCircleColor?: string | false;
  /** Whether to show the hover circle (default: true) */
  showHoverCircle?: boolean;
}

/**
 * Return value from the bounce link hook
 */
export interface UseBounceLinkReturn {
  /** Ref to attach to the link element */
  linkRef: RefObject<HTMLElement | null>;
  /** Ref to attach to the hover circle element */
  circleRef: RefObject<HTMLSpanElement | null>;
  /** Ref to attach to the original label element */
  labelRef: RefObject<HTMLSpanElement | null>;
  /** Ref to attach to the hover label element */
  hoverLabelRef: RefObject<HTMLSpanElement | null>;
  /** Handler to call on mouse enter */
  handleEnter: () => void;
  /** Handler to call on mouse leave */
  handleLeave: () => void;
}

/**
 * Hook for creating bounce link animations.
 *
 * Use this hook directly if you need more control over the link structure.
 * For most cases, use the AnimatedLink component instead.
 *
 * @example
 * ```tsx
 * const { linkRef, circleRef, labelRef, hoverLabelRef, handleEnter, handleLeave } = useBounceLink({
 *   hoverCircleColor: '#0b3b66',
 *   ease: 'power3.easeOut',
 *   duration: 2
 * });
 *
 * return (
 *   <a ref={linkRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
 *     <span ref={circleRef} className="hover-circle" />
 *     <span className="label-stack">
 *       <span ref={labelRef}>Link Text</span>
 *       <span ref={hoverLabelRef}>Link Text</span>
 *     </span>
 *   </a>
 * );
 * ```
 */
export function useBounceLink(options: UseBounceLinkOptions = {}): UseBounceLinkReturn {
  const { ease = "power3.easeOut", duration = 2, hoverCircleColor, showHoverCircle = true } = options;

  const linkRef = useRef<HTMLElement>(null);
  const circleRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const hoverLabelRef = useRef<HTMLSpanElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const activeTweenRef = useRef<gsap.core.Tween | null>(null);

  useEffect(() => {
    const layout = () => {
      const circle = circleRef.current;
      const link = linkRef.current;
      if (!link) return;

      // Read computed gap value and set as CSS variable
      const computedGap = window.getComputedStyle(link).gap;
      if (computedGap && computedGap !== "normal" && computedGap !== "0px") {
        link.style.setProperty("--animated-link-gap", computedGap);
      }

      const pill = link;
      const rect = pill.getBoundingClientRect();
      const { width: w, height: h } = rect;

      // Set up circle if it exists and should be shown
      if (circle && showHoverCircle) {
        // Calculate circle dimensions based on pill size
        const R = ((w * w) / 4 + h * h) / (2 * h);
        const D = Math.ceil(2 * R) + 2;
        const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
        const originY = D - delta;

        circle.style.width = `${D}px`;
        circle.style.height = `${D}px`;
        circle.style.bottom = `-${delta}px`;
        circle.style.display = "block";

        if (typeof hoverCircleColor === "string") {
          circle.style.backgroundColor = hoverCircleColor;
        } else if (hoverCircleColor === false || hoverCircleColor === "transparent") {
          circle.style.backgroundColor = "transparent";
        }

        gsap.set(circle, {
          xPercent: -50,
          scale: 0,
          transformOrigin: `50% ${originY}px`,
        });
      }

      const label = labelRef.current;
      const hoverLabel = hoverLabelRef.current;

      if (label) gsap.set(label, { y: 0 });
      if (hoverLabel) gsap.set(hoverLabel, { y: h + 12, opacity: 0 });

      // Kill existing timeline
      tlRef.current?.kill();

      // Create new timeline
      const tl = gsap.timeline({ paused: true });

      if (circle && showHoverCircle) {
        tl.to(
          circle,
          {
            scale: 1.2,
            xPercent: -50,
            duration,
            ease,
            overwrite: "auto",
          },
          0
        );
      }

      if (label) {
        tl.to(
          label,
          {
            y: -(h + 8),
            duration,
            ease,
            overwrite: "auto",
          },
          0
        );
      }

      if (hoverLabel) {
        gsap.set(hoverLabel, { y: Math.ceil(h + 100), opacity: 0 });
        tl.to(
          hoverLabel,
          {
            y: 0,
            opacity: 1,
            duration,
            ease,
            overwrite: "auto",
          },
          0
        );
      }

      tlRef.current = tl;
    };

    layout();

    const onResize = () => layout();
    window.addEventListener("resize", onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(layout).catch(() => {});
    }

    return () => {
      window.removeEventListener("resize", onResize);
      tlRef.current?.kill();
      activeTweenRef.current?.kill();
    };
  }, [ease, duration, hoverCircleColor, showHoverCircle]);

  const handleEnter = () => {
    const tl = tlRef.current;
    if (!tl) return;

    activeTweenRef.current?.kill();
    activeTweenRef.current = tl.tweenTo(tl.duration(), {
      duration: 0.3,
      ease,
      overwrite: "auto",
    });
  };

  const handleLeave = () => {
    const tl = tlRef.current;
    if (!tl) return;

    activeTweenRef.current?.kill();
    activeTweenRef.current = tl.tweenTo(0, {
      duration: 0.2,
      ease,
      overwrite: "auto",
    });
  };

  return {
    linkRef,
    circleRef,
    labelRef,
    hoverLabelRef,
    handleEnter,
    handleLeave,
  };
}
